import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import {
  ChevronDown,
  ChevronsUp,
  ChevronUp,
  Edit,
  Eye,
  ListCheck,
  Plus,
  Search,
} from 'lucide-react';

import StatusBadge from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TooltipButton } from '@/components/ui/tooltip-button';
import { useControls } from '@/hooks/useControlsAPI';
import { useNotificationModal } from '@/hooks/useNotificationModal';
import PageLayout from '@/layouts/PageLayout';
import { getUserInfo } from '@/lib/auth';
import { apiService } from '@/lib/axios';
import { cn } from '@/lib/utils';

const controlTypeMap = {
  INITIAL_RELEASE: 'Initial Release',
  NEW_DOCUMENT: 'New Document',
  PERIODIC: 'Periodic Review',
  REVISION: 'Revision',
};

const getControlActions = (
  controlItem,
  isMasterView,
  activeFilter,
  navigate,
  onReleaseClick
) => {
  const userSub = getUserInfo()?.sub;
  const isCreator = userSub === controlItem.requestorId;
  const isOther = !isMasterView && activeFilter === 'other';
  const isApprovedFilter = !isMasterView && activeFilter === 'approved';

  // In "other" filter, only show View Detail
  if (isOther)
    return [
      {
        icon: <Eye className="h-4 w-4" />,
        tooltip: 'View Detail',
        onClick: () => navigate(`/control/${controlItem.id}`),
      },
    ];

  const actions = [];

  // Edit — requestors can edit drafts and controls returned for revision
  if (
    !isMasterView &&
    ['DRAFT', 'RETURNED'].includes(controlItem.status) &&
    isCreator
  )
    actions.push({
      icon: <Edit className="h-4 w-4" />,
      tooltip: 'Edit CC',
      onClick: () => navigate(`/control/${controlItem.id}/edit`),
    });

  // View Detail / Preview
  actions.push({
    icon: <Eye className="h-4 w-4" />,
    tooltip: isMasterView ? 'Preview' : 'View Detail',
    onClick: () =>
      navigate(`/control/${controlItem.id}`, {
        state: isMasterView
          ? undefined
          : isApprovedFilter
            ? 'master'
            : undefined,
      }),
  });

  // View Training Progress
  actions.push({
    icon: <ListCheck className="h-4 w-4" />,
    tooltip: isMasterView ? 'View Training Progress' : 'Preview',
    onClick: () =>
      navigate(`/control/${controlItem.id}/progress`, {
        state: isMasterView ? 'master' : 'pending',
      }),
  });

  // Release — approved filter in unreleased view
  if (isApprovedFilter && controlItem.status === 'APPROVED')
    actions.push({
      icon: <ChevronsUp className="h-4 w-4" />,
      tooltip: 'Release',
      onClick: () => onReleaseClick?.(controlItem),
    });

  return actions;
};

const ControlListPage = () => {
  const [sortBy, setSortBy] = useState('serial');
  const [sortDirection, setSortDirection] = useState('asc');
  const location = useLocation();
  const navigate = useNavigate();
  const { showNotificationModal } = useNotificationModal();

  const [activeTab, setActiveTab] = useState(location.state || 'master');
  const [activeFilter, setActiveFilter] = useState('attention');
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingRelease, setPendingRelease] = useState(null);
  const [releaseStep, setReleaseStep] = useState(null); // 'ask' | 'confirm'
  const [releasing, setReleasing] = useState(false);

  const { controls } = useControls();

  const isMasterView = activeTab === 'master';
  const canFilter = getUserInfo()?.['cognito:groups']?.some((role) =>
    ['QA', 'PowerUser', 'SuperAdmin'].includes(role)
  );

  const userSub = getUserInfo()?.sub;
  const userGroups = getUserInfo()?.['cognito:groups'] || [];

  const getSearchableFields = (item, isMasterView) => {
    const fields = [item.series, item.requestor, item.type, item.status];

    // Include document field only on master tab
    if (isMasterView) fields.push(item.document);

    return fields;
  };

  const applySearch = (list, isMasterView) => {
    const searchLower = searchQuery.toLowerCase().trim();

    if (!searchLower) return list;

    return list.filter((item) =>
      getSearchableFields(item, isMasterView).some((field) =>
        String(field).toLowerCase().includes(searchLower)
      )
    );
  };

  const applySort = (list, sortBy, sortDirection) => {
    if (!sortBy) return list;

    return [...list].sort((a, b) => {
      const aVal = a[sortBy] ?? '';
      const bVal = b[sortBy] ?? '';
      const result = String(aVal).localeCompare(String(bVal));

      return sortDirection === 'asc' ? result : -result;
    });
  };

  const baseList = controls.filter((control) => {
    const { status } = control;

    if (isMasterView) {
      if (status === 'RELEASED') return true;

      if (status === 'REJECTED') return userGroups.includes('QA');

      return false;
    }

    switch (activeFilter) {
      case 'attention': {
        const getSub = (r) => (typeof r === 'object' ? r?.sub : r);

        if (status === 'REVIEWING')
          return (
            getSub(control.reviewerPipeline?.[control.currentActorIndex]) ===
            userSub
          );

        if (status === 'RETURNED') return control.requestorId === userSub;

        if (status === 'DRAFT') return control.requestorId === userSub;

        return false;
      }
      case 'approved':
        return status === 'APPROVED';

      case 'other': {
        // Admins can see all, but READ ONLY
        if (
          userGroups.some((role) => ['PowerUser', 'SuperAdmin'].includes(role))
        )
          return true;

        // Show all reviewing items where user is not the current actor
        if (status === 'REVIEWING') {
          const getSub = (r) => (typeof r === 'object' ? r?.sub : r);

          return (
            getSub(control.reviewerPipeline?.[control.currentActorIndex]) !==
            userSub
          );
        }

        // Show rejected / returned items
        if (status === 'REJECTED') return true;

        if (status === 'RETURNED') return control.requestorId !== userSub;

        // Show drafts not owned by current user
        if (status === 'DRAFT' && control.requestorId !== userSub) return true;

        return false;
      }

      default:
        return false;
    }
  });

  const controlList = applySort(
    applySearch(baseList, isMasterView),
    sortBy,
    sortDirection
  );

  const getSortIcon = (column) => {
    if (sortBy !== column)
      return <ChevronDown className="h-4 w-4 opacity-50" />;

    return sortDirection === 'asc' ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  const handleTab = (value) => {
    navigate('.', { replace: true, state: value });
    setActiveTab(value);
    setSearchQuery('');
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const handleReleaseClick = (controlItem) => {
    setPendingRelease(controlItem);
    setReleaseStep('ask');
  };

  const handleReleaseWithDocument = () => {
    const item = pendingRelease;

    if (!item) return;
    setPendingRelease(null);
    setReleaseStep(null);
    navigate(`/control/${item.id}/release`, {
      state: {
        serial: item.serial,
        type: item.type,
        requestor: item.requestor,
      },
    });
  };

  const handleReleaseWithoutDocument = async () => {
    if (!pendingRelease || releasing) return;

    setReleasing(true);

    try {
      const res = await apiService.controls.release(pendingRelease.id, {});

      if (res.data.success) {
        showNotificationModal(
          'Release Successful',
          `${pendingRelease.serial} released successfully`,
          'success'
        );
        setPendingRelease(null);
        setReleaseStep(null);
        navigate('/controls', { state: 'master' });
      }
    } catch (err) {
      const message =
        err?.response?.data?.error || 'Failed to release change control';
      showNotificationModal('Release Failed', message, 'error');
    } finally {
      setReleasing(false);
    }
  };

  return (
    <PageLayout
      title={
        isMasterView
          ? 'Change Control Form Master List'
          : 'Pending Change Control Form'
      }
      toolbarTail={
        <Button onClick={() => navigate('/control/new')} variant="outline">
          <Plus />
          Add CC
        </Button>
      }
    >
      <Tabs value={activeTab} onValueChange={handleTab}>
        <TabsList
          className={cn(
            'w-full flex gap-x-2 gap-y-1 flex-wrap',
            !isMasterView && 'rounded-b-none'
          )}
        >
          <TabsTrigger value="master">
            Change Control Form Master List
          </TabsTrigger>
          <TabsTrigger value="pending">Pending Change Control Form</TabsTrigger>
        </TabsList>
      </Tabs>
      {!isMasterView && (
        <Tabs onValueChange={setActiveFilter} value={activeFilter}>
          <TabsList
            className={cn(
              'w-full flex gap-x-2 gap-y-1 flex-wrap',
              'bg-gray-50 rounded-t-none'
            )}
          >
            <TabsTrigger value="attention">Your Attention Needed</TabsTrigger>
            <TabsTrigger value="approved">Approved Change Control</TabsTrigger>
            <TabsTrigger value="other">
              Other Pending Change Control
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      <div className="flex gap-4 items-center mt-4 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className={`pl-10 text-lg font-semibold ${false ? 'border-destructive' : ''}`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search across all fields..."
        />
        {isMasterView && canFilter && (
          <div className="flex gap-3 shrink-0">
            Rejected CC
            <Switch onCheckedChange={() => {}} />
          </div>
        )}
      </div>
      <div className="mt-4 rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[172px]">
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold hover:bg-transparent"
                  onClick={() => handleSort('serial')}
                >
                  CC Number
                  {getSortIcon('serial')}
                </Button>
              </TableHead>
              {isMasterView && (
                <TableHead>
                  <Button
                    variant="ghost"
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                    onClick={() => handleSort('document')}
                  >
                    Document Linked
                    {getSortIcon('document')}
                  </Button>
                </TableHead>
              )}
              {!isMasterView && (
                <TableHead>
                  <Button
                    variant="ghost"
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                    onClick={() => handleSort('requestorName')}
                  >
                    Requestor
                    {getSortIcon('requestorName')}
                  </Button>
                </TableHead>
              )}
              <TableHead>
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold hover:bg-transparent"
                  onClick={() =>
                    handleSort(
                      isMasterView ? 'informationReason' : 'typeChange'
                    )
                  }
                >
                  {isMasterView ? 'Reason for Change' : 'Type of Change'}
                  {getSortIcon(
                    isMasterView ? 'informationReason' : 'typeChange'
                  )}
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold hover:bg-transparent"
                  onClick={() => handleSort('status')}
                >
                  Status
                  {getSortIcon('status')}
                </Button>
              </TableHead>
              <TableHead
                className={cn(
                  'sticky right-0 z-10',
                  'w-[124px] font-semibold text-center',
                  'backdrop-blur bg-background/95 supports-[backdrop-filter]:bg-background/80',
                  'before:absolute before:h-full before:left-0 before:top-0 before:w-px',
                  'before:bg-gray-200'
                )}
              >
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {controlList.map((controlItem) => (
              <TableRow key={controlItem.id}>
                <TableCell>{controlItem.serial}</TableCell>
                {isMasterView && (
                  <TableCell>
                    {controlItem.document ? (
                      <Link
                        className="font-medium text-primary underline-offset-4 hover:underline"
                        state={{ source: 'master' }}
                        to={`/document/${controlItem.document.id}`}
                      >
                        {controlItem.document.title}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                )}
                {!isMasterView && (
                  <TableCell>
                    {controlItem.requestorName || controlItem.requestorId}
                  </TableCell>
                )}
                <TableCell>
                  {isMasterView
                    ? controlItem.informationReason
                    : controlTypeMap[controlItem.typeChange]}
                </TableCell>
                <TableCell>
                  <StatusBadge status={controlItem.status} />
                </TableCell>
                <TableCell
                  className={cn(
                    'sticky right-0 z-10',
                    'space-x-2 whitespace-nowrap',
                    'after:absolute after:h-px after:left-0 after:-top-px after:w-full',
                    'before:absolute before:h-full before:left-0 before:top-0 before:w-px',
                    'after:bg-gray-200 before:bg-gray-200'
                  )}
                >
                  {getControlActions(
                    controlItem,
                    isMasterView,
                    activeFilter,
                    navigate,
                    handleReleaseClick
                  ).map((action) => (
                    <TooltipButton
                      key={action.tooltip}
                      onClick={action.onClick}
                      tooltip={action.tooltip}
                      variant="outline"
                    >
                      {action.icon}
                    </TooltipButton>
                  ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!controlList.length && (
          <div className="py-8 text-center text-gray-400">No Data</div>
        )}
      </div>

      <Dialog
        open={!!pendingRelease && releaseStep === 'ask'}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setPendingRelease(null);
            setReleaseStep(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Releasing Change Control {pendingRelease?.serial}
            </DialogTitle>
            <DialogDescription>
              Releasing Change Control {pendingRelease?.serial} alone or with a
              Document?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReleaseStep('confirm')}>
              Alone
            </Button>
            <Button onClick={handleReleaseWithDocument}>With a Document</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!pendingRelease && releaseStep === 'confirm'}
        onOpenChange={(isOpen) => {
          if (!isOpen && !releasing) {
            setPendingRelease(null);
            setReleaseStep(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Release Without Document</DialogTitle>
            <DialogDescription>
              You are about to release Change Control {pendingRelease?.serial}{' '}
              without an accompanied Document. This action cannot be undone.
              <br />
              Please verify that {pendingRelease?.serial} is the correct Change
              Control you intend to release. If you are uncertain, select
              &quot;No&quot; and review the Change Control before proceeding.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={releasing}
              onClick={() => {
                setPendingRelease(null);
                setReleaseStep(null);
              }}
            >
              No, Go Back
            </Button>
            <Button disabled={releasing} onClick={handleReleaseWithoutDocument}>
              {releasing ? 'Releasing...' : 'Yes, Release'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default ControlListPage;
